import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { RootState } from "..";
import { ApiFactory } from "../../api";
import { VerwerkingsoverzichtResults } from "../../models/verwerkingsoverzicht";
import { ReducerErrorToaster } from "../../utils/ReducerUtils";

interface VerwerkingsoverzichtState {
  fetching: boolean;
  error: boolean;
  data: VerwerkingsoverzichtResults;
  bannerDismissed: boolean;
  heeftFouten: boolean;
  pageSize: number;
}

const initialState: VerwerkingsoverzichtState = {
  fetching: false,
  error: false,
  data: {
    aantal: 0,
    resultaten: [],
    start: 0,
    totaal: 0,
  },
  bannerDismissed: false,
  heeftFouten: false,
  pageSize: 25,
};

export const getVerwerkingsoverzicht = createAsyncThunk<
  VerwerkingsoverzichtResults,
  { pagina: number },
  { state: RootState }
>("verwerkingsoverzicht/get", async (args: { pagina: number }, thunkAPI) => {
  const api = ApiFactory.createVerwerkingsoverzichtApi();
  const { pageSize } = thunkAPI.getState().verwerkingsoverzicht;

  try {
    return await api.getVerwerkingsoverzicht((args.pagina - 1) * pageSize, pageSize, "desc", [
      "verwerkingsstatusInverwerkingOfFout",
    ]);
  } catch (error: unknown) {
    const normalizedError = error instanceof Error ? error : new Error(String(error));
    ReducerErrorToaster(thunkAPI, normalizedError);
    return thunkAPI.rejectWithValue(normalizedError.message);
  }
});

export const checkVerwerkingsfouten = createAsyncThunk<boolean, void, { state: RootState }>(
  "verwerkingsoverzicht/checkFouten",
  async (_, thunkAPI) => {
    const api = ApiFactory.createVerwerkingsoverzichtApi();

    try {
      const { totaal } = await api.getVerwerkingsoverzicht(0, 1, "desc", ["verwerkingsstatusFout"]);
      return totaal > 0;
    } catch (error: unknown) {
      const normalizedError = error instanceof Error ? error : new Error(String(error));
      return thunkAPI.rejectWithValue(normalizedError.message);
    }
  }
);

const verwerkingsoverzichtState = createSlice({
  name: "verwerkingsoverzicht",
  initialState,
  reducers: {
    dismissBanner: (state) => {
      state.bannerDismissed = true;
    },
  },
  extraReducers: (caseBuilder) => {
    caseBuilder.addCase(getVerwerkingsoverzicht.pending, (state) => {
      state.fetching = true;
      state.error = false;
    });
    caseBuilder.addCase(getVerwerkingsoverzicht.rejected, (state) => {
      state.fetching = false;
      state.error = true;
    });
    caseBuilder.addCase(getVerwerkingsoverzicht.fulfilled, (state, action) => {
      state.fetching = false;
      state.error = false;
      state.data = {
        ...action.payload,
        resultaten: [...action.payload.resultaten],
      };
    });
    caseBuilder.addCase(checkVerwerkingsfouten.fulfilled, (state, action) => {
      state.heeftFouten = action.payload;
    });
  },
});

export const { dismissBanner } = verwerkingsoverzichtState.actions;
export default verwerkingsoverzichtState.reducer;
